import { OperationVariables, TypedDocumentNode, gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useEffect, useState } from 'react';

import { AppointmentInteractionEventLoading } from '@generated/gql/graphql';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useSyncedRequestContext } from '@providers/AppointmentProvider/SyncedRequestContext';
import { useAppointmentId } from '@store/appointment/selectors';

const requestLoadingMutation = gql(`
  mutation AppointmentUpdateLoadingState($id: ID!, $request: String!, $isLoading: Boolean!) {
    appointmentUpdateLoadingState(id: $id, request: $request, isLoading: $isLoading)
  }
`);

enum RequestID {
  BASKET_CREATE = 'BASKET_CREATE',
  BASKET_FETCH = 'BASKET_FETCH',
  ENERGY_DETAILS_SAVE = 'ENERGY_DETAILS_SAVE',
  ENERGY_QUOTE_SELECT = 'ENERGY_QUOTE_SELECT',
  UPDATE_ENERGY_TARIFF = 'UPDATE_ENERGY_TARIFF',
  MOBILE_ADD = 'MOBILE_ADD',
  MOBILE_REMOVE = 'MOBILE_REMOVE',
}

interface RequestState {
  isLoading: boolean;
}

type RequestStates = {
  [id: string]: RequestState;
};

const initialStore = Object.values(RequestID).reduce((acc, id) => {
  acc[id] = {
    isLoading: false,
  };
  return acc;
}, {} as RequestStates);

const useRequestStates = () => {
  const [requestStates, setRequestStates] =
    useState<RequestStates>(initialStore);
  const setLoading = (event: AppointmentInteractionEventLoading) => {
    const { request, isLoading } = event;
    setRequestStates({ ...requestStates, [request]: { isLoading } });
  };
  return { requestStates, setLoading };
};

const useSyncedMutation = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: TypedDocumentNode<TData, TVariables>,
  id: RequestID,
  options?: useMutation.Options<TData, TVariables>
): useMutation.ResultTuple<TData, TVariables> => {
  const appointmentId = useAppointmentId();
  const [requestLoading] = useMutation(requestLoadingMutation);
  const { hasControl } = useAppointmentContext();
  const { syncedRequestStates } = useSyncedRequestContext();
  const mutation = useMutation<TData, TVariables>(query, options);

  const isLoading = mutation[1].loading;

  useEffect(() => {
    if (!appointmentId || !hasControl || !id) {
      return;
    }

    requestLoading({
      variables: {
        id: appointmentId,
        request: id,
        isLoading,
      },
    });
  }, [appointmentId, hasControl, id, isLoading, requestLoading]);

  if (!appointmentId || hasControl) {
    return mutation;
  }

  mutation[1].loading = isLoading || syncedRequestStates[id].isLoading;

  return mutation;
};

export {
  useSyncedMutation,
  requestLoadingMutation,
  useRequestStates,
  RequestID,
};

export type { RequestStates };
